import { useQuery } from '@tanstack/react-query';
import { http } from './http';

export interface LeaderboardEntry {
  rank: number;
  wallet: string;
  level: number;
  xp: number;
  eloRating: number;
  totalWins: number;
  totalGames: number;
  currentStreak: number;
  bestStreak: number;
  rankTier: number;
  rankName: string;
  lastPlayedAt: string | null;
  displayName?: string;
}

export type LeaderboardType = 'elo' | 'wins' | 'level';

export const useLeaderboard = (type: LeaderboardType = 'elo', limit: number = 100) =>
  useQuery({
    queryKey: ['leaderboard', type, limit],
    queryFn: () =>
      http.get<{ leaderboard: LeaderboardEntry[] }>(`/leaderboard?type=${type}&limit=${limit}`),
    select: (data) => data.leaderboard,
    staleTime: 30_000, // Consider data stale after 30s
    refetchOnMount: true, // Only refetch if stale (instant for cached data)
    refetchInterval: 60_000, // Poll every 60s for updates
    placeholderData: (previousData) => previousData, // Smooth transitions between tabs
  });

export interface PlayerStats {
  wallet: string;
  xp: number;
  level: number;
  eloRating: number;
  totalWins: number;
  totalLosses: number;
  totalGames: number;
  currentStreak: number;
  bestStreak: number;
  rankTier: number;
  rankName: string;
  globalRank: number;
  achievements: string[];
  loginStreak: number;
}

export const usePlayerStats = (wallet: string | null | undefined) =>
  useQuery({
    queryKey: ['player', 'stats', wallet],
    enabled: Boolean(wallet),
    queryFn: () => http.get<{ stats: PlayerStats }>(`/players/${wallet}/stats`),
    select: (data) => data.stats,
    staleTime: 10_000,
    refetchOnMount: true, // Only refetch if stale
  });
